import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { catchError, filter, map, of, startWith } from 'rxjs';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { BusinessAccountService } from '@/app/services/business-account/business-account.service';
import { ImageService } from '@/app/services/image/image.service';
import {
  brandInsightsNavItems,
  BrandInsightsNavItem,
  BrandInsightsSection,
} from '../../insights-nav';
import { BrandInsightsState } from './brand-insights.state';

@Component({
  selector: 'ins-brand',
  imports: [RouterOutlet],
  templateUrl: './brand.html',
  styleUrl: './brand.css',
  host: { class: 'flex flex-col h-full min-h-0 flex-1 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Brand implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private businessAccountService = inject(BusinessAccountService);
  readonly imageService = inject(ImageService);
  readonly brandState = inject(BrandInsightsState);

  readonly businessAccountId = signal<string | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly navItems = brandInsightsNavItems;

  readonly brand = this.brandState.brand;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split('?')[0] ?? ''),
      startWith(this.router.url.split('?')[0] ?? ''),
    ),
    { initialValue: this.router.url.split('?')[0] ?? '' },
  );

  readonly activeSection = computed((): BrandInsightsSection | null => {
    const url = this.currentUrl();
    const match = url.match(/\/insights\/brand\/[^/]+\/([^/?]+)/);
    return (match?.[1] as BrandInsightsSection) ?? null;
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('businessAccountId') ?? undefined;
      this.businessAccountId.set(id);
      if (id) {
        this.loadBrand(id);
      }
    });
  }

  goToSection(item: BrandInsightsNavItem): void {
    const id = this.businessAccountId();
    if (!id) {
      return;
    }
    void this.router.navigate(['/insights/brand', id, item.section]);
  }

  getNavClass(item: BrandInsightsNavItem): string {
    if (this.activeSection() === item.section) {
      return 'border-primary text-accent';
    }
    return 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300';
  }

  getNavIconClass(item: BrandInsightsNavItem): string {
    if (this.activeSection() === item.section) {
      return 'text-accent mr-1.5';
    }
    return 'text-gray-400 group-hover:text-gray-500';
  }

  private loadBrand(businessAccountId: string): void {
    const current = this.brandState.brand();
    if (current?.id === businessAccountId) {
      this.loadingState.set('loaded');
      return;
    }

    this.loadingState.set('loading');
    this.businessAccountService
      .getBusinessAccountById(businessAccountId)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((brand) => {
        if (brand) {
          this.brandState.setBrand(brand as BusinessAccount);
          this.loadingState.set('loaded');
        }
      });
  }
}
